import { useRef, useEffect, useCallback } from 'react';

export function useInterval(callback: () => void, delay: number | undefined) {
  const intervalRef = useRef<number | null>(null);
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const tick = () => savedCallback.current();

    if (typeof delay === 'number') {
      intervalRef.current = window.setInterval(tick, delay);

      if (intervalRef.current) {
        return () => window.clearInterval(intervalRef.current || undefined); // || undefined for typescript
      }
    }

    return undefined;
  }, [delay]);

  return intervalRef;
}

/**
 * Use when you need to start and stop an interval at arbitrary points of time in a component's life
 */
export const useStoppableInterval = (enabled: boolean, callback: () => void, delay: number, max?: number) => {
  const intervalRef = useRef<number | null>(null);
  const countRef = useRef(0);

  const stopInterval = useCallback(() => {
    if (intervalRef.current === null) {
      return false;
    }

    window.clearInterval(intervalRef.current ?? 0);
    intervalRef.current = null;
    countRef.current = 0;

    return true;
  }, []);

  useEffect(() => {
    // disabled and running => stop
    if (!enabled && intervalRef.current !== null) {
      stopInterval();

      return () => window.clearInterval(intervalRef.current ?? 0);
    }

    // enabled and not running => start
    if (enabled && intervalRef.current === null && (!max || countRef.current < max)) {
      intervalRef.current = window.setInterval(() => {
        countRef.current += 1;

        if (max && countRef.current >= max) {
          stopInterval();

          return;
        }

        callback();
      }, delay);
    }

    return () => window.clearInterval(intervalRef.current ?? 0);
  }, [enabled, callback, delay, max, stopInterval]);

  return stopInterval;
};
